import { createSelector } from "@reduxjs/toolkit";
import { generatePath } from "react-router-dom";
import { RootState } from "@store/index";
import { DataRoomFund } from "@dataRoomSetup/models";
import { dataRoomsSelector } from "@dataRooms/store/selectors";
import { AppRoute } from "@app/models/AppRoute";

export const openDataRoomSetupDialogFlagSelector = (state: RootState): DataRoomFund["isOpenDataRoomDialog"] =>
  state.dataRoomSetup.isOpenDataRoomDialog;

export const isDataRoomEditable = (state: RootState): DataRoomFund["dataRoomEditable"] =>
  state.dataRoomSetup.dataRoomEditable;

export const dataRoomSetupSelector = createSelector(
  openDataRoomSetupDialogFlagSelector,
  (isOpenDataRoomSetupDialog) => ({
    isOpenDataRoomSetupDialog,
  })
);

export const dataRoomSetupFundSelector = createSelector(dataRoomsSelector, (funds) =>
  funds
    .map((fund) => ({
      label: fund.title,
      id: generatePath(AppRoute.DataRoom, { globalId: fund.globalId }),
    }))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
);

// Typed folder-state selectors. These replace inline `(state: any)` selectors
// scattered across the DataRoomSetup components so the folder slice shape stays
// in one place and is fully typed via RootState.
export const selectFolders = (state: RootState) => state.folders.folders;

export const selectSelectedFiles = (state: RootState) => state.folders.selectedFiles;

export const selectSelectedFilesSourceFolder = (state: RootState) => state.folders.selectedFilesSourceFolder;

export const selectSelectedSourceFolderIds = (state: RootState) => state.folders.selectedSourceFolderIds;

export const selectExpandedFolders = (state: RootState) => state.folders.expandedFolders;

export const selectRenamingFolderId = (state: RootState) => state.folders.ui.renamingFolderId;

export const selectRootReorderFolderId = (state: RootState) => state.folders.ui.rootReorderFolderId;

export const selectSetupDocuments = (state: RootState) => state.dataRoomSetupDocuments.setupDocument;

export const selectRawFolders = (state: RootState) => state.folders.rawFolders;

export const selectShowHiddenDefaults = (state: RootState) => state.folders.showHiddenDefaults;

/**
 * Folders to display in the tree.
 *
 * Display only — the Publish payload always uses `state.folders`, which is never
 * mutated here.
 *
 * Default root folders that are empty and backend-hidden (`isDisplay === false`)
 * are filtered out of `state.folders` at fetch time. To support the "Show hidden
 * defaults" toggle, those filtered-out default roots are merged back in from
 * `rawFolders` (only while the toggle is on). `FolderTree` then renders them
 * dimmed with the Hidden chip via its empty-folder visibility rules.
 */
export const selectVisibleFolders = createSelector(
  [selectFolders, selectRawFolders, selectShowHiddenDefaults],
  (folders, rawFolders, showHiddenDefaults) => {
    if (!showHiddenDefaults) {
      return folders;
    }

    const presentIds = new Set(folders.map((folder) => folder.detailId));
    const hiddenDefaultRoots = rawFolders.filter(
      (folder) => folder.parentId === null && !!folder.systemPreferences && !presentIds.has(folder.detailId)
    );

    if (hiddenDefaultRoots.length === 0) {
      return folders;
    }

    // Merge and sort once by sortOrder so re-introduced hidden default roots land
    // in their default position (AC5), regardless of whether a downstream consumer
    // re-sorts. This keeps this selector the single source of truth for order.
    return [...folders, ...hiddenDefaultRoots].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }
);
